import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import {
  AddStateToTransactions,
  CreateNewTransaction,
  SetTransactionAsCurrent,
  UpdateTransactionState,
} from '@store/actions/adapted-state/adapted-state.actions';
import { UpdateInvestmentObjectives } from '@store/actions/investment-objectives/investment-objectives.action';
import * as fromRootStore from '@store/index';
import { CheckFirstNewAccountTransaction } from '@store/index';
import {
  selectIsFirstNewAccountTransactionExist,
  selectIsNewAccountTransactionExist,
  selectNewAccountTransaction,
} from '@store/selectors/transaction-state/transaction-state.selectors';
import { filter, map, switchMap, withLatestFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  ResetEmploymentState,
  ResetFinancials,
  UpdateInformationIsCorrect,
  UpdatePersonalInfoClientValidity,
} from 'vg-interaction-model';

import {
  AccountTriageContinue,
  ClearNewAccountData,
  LogInfo,
  SetFirstNewAccountTransaction,
} from '../../actions';
import { Person } from '../../reducers/beneficiaries/beneficiaries.reducer';
import { State } from '../../reducers/index';

@Injectable()
export class AccountTriageEffects {
  constructor(
    private actions$: Actions,
    private globalStore$: Store<State>,
  ) {}

  accountTriageContinue$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountTriageContinue),
        withLatestFrom(
          this.globalStore$.pipe(select(selectIsNewAccountTransactionExist)),
          this.globalStore$.pipe(select(selectIsFirstNewAccountTransactionExist)),
        ),
        switchMap(([, isNewAccountTransactionExist, isFirstNewAccountTransactionExist]) => {
          const actions: Action[] = [];

          LogInfo({
            payload: {
              eventId: 'account-triage-continue-effect-started',
              message: 'Account triage continue effect start',
            },
          });

          if (isNewAccountTransactionExist && !isFirstNewAccountTransactionExist) {
            actions.push(SetFirstNewAccountTransaction());
          }

          if (!isNewAccountTransactionExist) {
            actions.push(ClearNewAccountData());
          }

          return actions;
        }),
      ),
    { dispatch: true },
  );

  accountTriageClearNewAccountData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClearNewAccountData),
        withLatestFrom([this.globalStore$.pipe(select(selectIsNewAccountTransactionExist))]),
        filter(([, isNewAccountTransactionExist]) => !!isNewAccountTransactionExist),
        switchMap(() => {
          let actions: Action[] = [];

          LogInfo({
            payload: {
              eventId: 'account-triage-clear-new-account-data-effect-started',
              message: 'Account triage clear new account data effect start',
            },
          });

          actions = actions.concat([
            UpdateInformationIsCorrect({ payload: null }),
            UpdatePersonalInfoClientValidity({ payload: 'INVALID' }),
            new ResetEmploymentState(),
            new ResetFinancials(),
            UpdateInvestmentObjectives({
              objectives: { CAP: null, Income: null, Growth: null, Speculation: null },
            }),
          ]);

          return actions;
        }),
      ),
    { dispatch: true },
  );

  setFirstNewAccountTransaction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SetFirstNewAccountTransaction),
        withLatestFrom(this.globalStore$.pipe(select(selectNewAccountTransaction))),
        switchMap(([, newAccountTransaction]) => {
          const changes: Partial<fromRootStore.TransactionState> = {
            firstNewAccount: true,
          };

          const actions: Action[] = [];

          LogInfo({
            payload: {
              eventId: 'account-triage-set-first-new-account-transaction-effect-started',
              message: 'Account triage set first new account effect start',
            },
          });

          if (newAccountTransaction) {
            const update = {
              id: newAccountTransaction.currentTransactionId,
              changes,
            };
            actions.push(UpdateTransactionState({ update }));
          }

          return actions;
        }),
      ),
    { dispatch: true },
  );

  checkFirstNewAccountTransaction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckFirstNewAccountTransaction),
        withLatestFrom(
          this.globalStore$.pipe(select(selectNewAccountTransaction)),
          this.globalStore$.pipe(select(selectIsFirstNewAccountTransactionExist)),
        ),
        filter(([, , isFirstNewAccountTransactionExist]) => !isFirstNewAccountTransactionExist),
        switchMap(([, newAccountTransaction]) => {
          const actions: Action[] = [];

          LogInfo({
            payload: {
              eventId: 'account-triage-check-first-new-account-transaction-effect-started',
              message: 'Account triage check first new account effect start',
            },
          });

          if (newAccountTransaction) {
            actions.push(SetFirstNewAccountTransaction());
          } else {
            actions.push(ClearNewAccountData());
          }

          return actions;
        }),
      ),
    { dispatch: true },
  );

  /**
   * Creates and initializes a new transaction in the store
   *
   * This effect generates a new UUID for the transaction, sets it as the current
   * transaction, and adds an initial empty state to the transactions store.
   *
   * Dispatches:
   * - SetTransactionAsCurrent: Sets the new transaction as current
   * - AddStateToTransactions: Adds the initial transaction state
   *
   * @listens CreateNewTransaction action with optional currentTransactionEditMode
   *
   * @example
   * ```typescript
   * // In a component
   * this.globalStore.dispatch(CreateNewTransaction({ currentTransactionEditMode: false }));
   * ```
   */
  createNewTransaction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CreateNewTransaction),
        map(({ currentTransactionEditMode = false }) => {
          const currentTransactionId = uuidv4();

          LogInfo({
            payload: {
              eventId: 'account-triage-create-new-transaction-effect-started',
              message: `Creating new transaction with ID: ${currentTransactionId}`,
            },
          });

          const actions: Action[] = [
            SetTransactionAsCurrent({
              transaction: {
                currentTransactionId,
              } as fromRootStore.TransactionState,
            }),
            AddStateToTransactions({
              state: {
                accountTriage: {},
                beneficiaries: [] as Person[],
                selectedClientAccounts: {},
                currentTransactionId,
                currentTransactionEditMode,
                firstNewAccount: false,
                settlementFund: null,
                estate: {},
              } as fromRootStore.TransactionState,
            }),
          ];

          return actions;
        }),
        switchMap((actions) => actions),
      ),
    { dispatch: true },
  );
}

export const effects: any[] = [AccountTriageEffects];
